import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { Box, MenuItem, Paper } from '@mui/material';
import { Popover } from '@cowbell/ui';
import { withStyles } from '@mui/styles';

import { Checkbox } from '../../inputs';
import SelectClasses from './MultiSelectStyles';

/** Above the MUI `Dialog` these open inside, which sits at MUI's `zIndex.modal` of 1300. */
const MENU_ABOVE_DIALOG_Z = 1400;

// @ts-expect-error TS(2345): Argument of type '({ config, palette, ...theme }: ... Remove this comment to see the full error message
export const Menu = withStyles(SelectClasses)((props: any) => {
  return (
    <Paper square className={props.classes.paper}>
      {props.children}
    </Paper>
  );
});

/**
 * The trigger, and the menu it opens.
 *
 * <p>The menu is a popover rather than an absolutely-positioned box beside the trigger. In flow it
 * was clipped by any ancestor that scrolls -- inside the Create User dialog the role and authority
 * level menus were cut off at the footer divider, with the options below simply unreachable.
 * Clipping is not stacking, so no z-index would have helped; the menu has to leave the subtree,
 * and only a portal does that.
 *
 * <p>`zIndex` is the part that *is* stacking. Mantine portals every dropdown into one shared node
 * that is a sibling of the dialog, not a descendant, and defaults it to 300 -- behind an MUI
 * `Dialog` at 1300. 1400 is what every other Mantine combobox in these dialogs uses.
 *
 * <p>The target is wrapped in a plain `div` rather than handed to `Popover.Target` directly: the
 * trigger is whatever the caller passed, thirty of them, and a component that does not forward its
 * ref leaves the dropdown anchored to the corner of the viewport. A `div` always forwards one.
 */
export function Dropdown({ children, isOpen, target, onClose }: any) {
  return (
    <Popover
      opened={Boolean(isOpen)}
      onDismiss={onClose}
      position="bottom-start"
      withinPortal
      zIndex={MENU_ABOVE_DIALOG_Z}
      // The search box inside the menu has to be typeable, and the menu opens inside dialogs that
      // pull focus back to themselves.
      trapFocus
      // Matched to the trigger, as the old in-flow menu got from `left: 0; right: 0` against the
      // trigger's own relative box.
      width="target"
      shadow="md"
      radius={0}
    >
      <Popover.Target>
        <div style={{ position: 'relative' }}>{target}</div>
      </Popover.Target>
      <Popover.Dropdown p={0}>{children}</Popover.Dropdown>
    </Popover>
  );
}

export function DropdownIndicator() {
  return <SearchIcon />;
}

export function Option(props: any) {
  return (
    <MenuItem
      className={props.selectProps.classes.menuItem}
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.isMulti ? (
        <React.Fragment>
          <Checkbox checked={props.isSelected} /> {props.children}
        </React.Fragment>
      ) : (
        <div
          style={{
            paddingLeft: '10px',
            paddingRight: '10px',
          }}
        >
          {props.children}
        </div>
      )}
    </MenuItem>
  );
}

// @ts-expect-error TS(2345): Argument of type '({ config, palette, ...theme }: ... Remove this comment to see the full error message
export const MenuList = withStyles(SelectClasses)((props: any) => {
  const { selectProps } = props;
  const { value, options } = selectProps;
  const allSelected = value.length === options.length;

  return (
    <div
      className={`${props.selectProps.className ?? ''} ${
        props.selectProps.classes.menuList
      }`}
      id="menu-list"
    >
      {props.selectProps.supportSelectAll && (
        <Box
          className={props.classes.newLegendItem}
          onClick={selectProps.toggleAllHandler}
        >
          <Checkbox checked={allSelected} />

          <p className={props.classes.legendText}>
            {allSelected ? 'Unselect All' : 'Select All'}
          </p>
        </Box>
      )}
      {props.children}
    </div>
  );
});
